import { stringify } from '../../../util/urlHelpers';
import { getCurrentSEOPageType } from '../../../containers/SearchPage/SearchPage.shared';
import { getLocaleFromUrl, IS_PRODUCTION } from '../../../constants/locales.constants';
import { MARKETPLACE_PATH } from '../../../constants/marketplacePath';
import { getTranslatedCategorySlug } from '../../../constants/categorySlug.constants';

/**
 * Applies new search params after a category-bearing filter changed, keeping the
 * visitor on the right SEO page:
 *
 * - on a category page, choosing another category moves to that category's page;
 * - on a brand page, the brand slug stays and the category goes in the query;
 * - anywhere else the params are pushed onto the current path.
 *
 * `selectedCategory` is the category that decides the page (the first one
 * chosen), or null when the change carries no category.
 *
 * Returns `{ redirected, search }` so callers can report what was navigated to.
 */
export const navigateWithCategory = ({
  history,
  match,
  pathname,
  state,
  params,
  selectedCategory,
}) => {
  if (selectedCategory) {
    const locale = getLocaleFromUrl();
    const currentSEOPage = getCurrentSEOPageType(match, locale);
    const localePrefix = IS_PRODUCTION
      ? MARKETPLACE_PATH
      : locale
      ? `${MARKETPLACE_PATH}/${locale}`
      : '';

    if (currentSEOPage && currentSEOPage.type === 'category') {
      const categorySlug = getTranslatedCategorySlug(selectedCategory, locale);
      if (categorySlug && categorySlug.toLowerCase() !== currentSEOPage.slug.toLowerCase()) {
        const { page: _, ...otherParams } = params;
        const queryString = Object.keys(otherParams).length > 0 ? `?${stringify(otherParams)}` : '';
        const target = `${localePrefix}/${categorySlug}${queryString}`;
        history.push(target);
        return { redirected: true, search: target };
      }
    } else if (currentSEOPage && currentSEOPage.type === 'brand') {
      const search = `?${stringify(params)}`;
      history.replace(`${localePrefix}/${currentSEOPage.slug}${search}`, state);
      return { redirected: false, search };
    }
  }

  const search = `?${stringify(params)}`;
  history.push(`${pathname}${search}`, state);
  return { redirected: false, search };
};
